/* eslint-disable max-lines */
import React, { useEffect } from 'react';
import styled from 'styled-components';
import { node, object } from 'prop-types';

import { useTranslate } from '../../translate/I18n';
import { getDisplayWorkTime, getMissionDate, pathWithParams } from '../../utils/utils';
import { BEELANCE, MISSION } from '../../constants';

import Box from '../atoms/Box';
import Card from '../atoms/Card';
import Separator from '../atoms/Separator';
import Text from '../atoms/Text';
import SkillReadonly from '../atoms/SkillReadonly';

import SanitizedHTML from '../elements/SanitizedHTML';
import { isInBrowser } from '../../utils/reactOrNext';
import { LocationEntity } from '../../utils/entity/locationEntity';
import Avatar from '../elements/Avatar';
import companyRoutes from '../../routes/company';
import {
  FacebookIcon,
  FacebookShareButton,
  TwitterIcon,
  TwitterShareButton,
  WhatsappIcon,
  WhatsappShareButton
} from 'react-share';
import { emitEvent, EVENTS } from '../../utils/analytics';
import { useMoney } from '../../hooks/useMoney';
import commonRoutes from '../../routes/common';
import Button from '../atoms/Button';
import { useRouter } from '../../utils/router';
import ContentIO from '../territory/ContentIO';
import CopyrightCalculatorButton from '../copyrightCalculator/CopyrightCalculatorButton';
import { getAppUrl } from '../../utils/config';
import { useUserContext } from '../auth/hook/userContext';
import dynamic from 'next/dynamic';
import MissionViewedDispatcher from './MissionViewedDispatcher';
import GoBack from '../elements/GoBack';
import Link from '../atoms/Link';
import { translateRoute } from '../../routesActions/routeHelper';

const {
  general: { login: routeLogin }
} = commonRoutes;

const ModalIncentivizeToRegister = dynamic(() => import('./ModalIncentivizeToRegister'));

const { routeUrl } = BEELANCE;

const { FULL_REMOTE, PARTIAL_REMOTE, ON_SITE } = MISSION;
const {
  PRICING_MODEL: { DAILY_RATE }
} = MISSION;

const StyledList = styled('ul')(({ theme }) => ({
  padding: `0 ${theme.space[2]}px ${theme.space[2]}px`,
  '& li': {
    display: 'flex',
    padding: `${theme.space[2]}px ${theme.space[3]}px`,
    borderRadius: theme.radii[0]
  },
  '& li:nth-child(even)': {
    background: theme.colors.greyVerylight
  },
  '& li p': {
    flex: 2,
    '&:first-of-type': {
      flex: 3
    }
  }
}));

const IconBorder = ({ children }) => (
  <Box border="solid 2px" borderColor="#F1F1F1" mr={1} borderRadius="13px" width="26px" height="26px" px="5px" py="2px">
    {children}
  </Box>
);

IconBorder.propTypes = {
  children: node
};

// eslint-disable-next-line
export const MissionSEO = ({ preloadedMission }) => {
  const { t, i18n } = useTranslate(['mission', 'common', 'sectors']);
  const history = useRouter();
  const mission = preloadedMission;
  const user = useUserContext();
  const money = useMoney();

  useEffect(() => {
    if (mission?.id && isInBrowser()) {
      MissionViewedDispatcher.dispatch(mission.id, mission.title, window.location.href);
    }
  }, [mission]);

  const {
    description = '',
    extraTime,
    workingStyle,
    company,
    skills = {},
    title = '',
    numberOfRemoteDaysAllowed,
    pricingModel,
    dailyRateRange,
    fixedPrice,
    startDate,
    endDate,
    currency,
    weeklyPlanning
  } = mission;

  const { name: companyName, logoLink: companyLogoLink, id: companyId } = company || {};

  const { sector, cfma } = company || {};
  const cfmaValue = cfma === null ? 'notDefinedCFMA' : cfma ? 'withCFMA' : 'withoutCFMA';

  const location = new LocationEntity(mission.locationDetails);

  const sharableMissionUrl = `${routeUrl}${pathWithParams(commonRoutes.missionSlugWithCompany.to, {
    missionSlugWithCompany: mission.slugWithCompany
  })}`;

  const loginPathWithReturnUrl = `${routeLogin.to}?returnUrl=m/${mission.slugWithCompany}`;

  return (
    <Box bg="whiteGrey" minHeight={'calc(100vh)'} p={{ _: 2, md: 3 }}>
      <Box maxWidth="1280px" margin="auto">
        <Box display="flex" justifyContent="space-between" mb={2}>
          <GoBack />
        </Box>

        {isInBrowser() && !window.document.title && <title>{title}</title>}

        <Box display="flex" width="100%" justifyContent="space-between" mb={2}>
          <Text tag="h1" mb={1} styleName="pH1">
            {title}
          </Text>
        </Box>

        <Box display="flex" alignItems="start" flexDirection={{ _: 'column', lg: 'row' }}>
          <Box flex="1" mr={{ _: 0, lg: 2, xl: 4 }}>
            <Card mb={4}>
              <Box px={3} pt={3}>
                {sector && (
                  <Box display="flex" alignItems="center" mb={3}>
                    <img width="14" height="14" src="/assets/icons/flag.png" alt="Sector" />
                    <Text styleName="pLabelPrimary" ml={2}>
                      {t(`sectors:${sector}`)}
                    </Text>
                  </Box>
                )}

                {mission.role && (
                  <Box display="flex" alignItems="center" mb={3}>
                    <img width="14" height="14" src="/assets/icons/user.png" alt="Role" />
                    <Text data-cy="matching-role" styleName="pLabelPrimary" ml={2}>
                      {t(`mission:matchingRoles:${mission.role.key}`)}
                    </Text>
                  </Box>
                )}

                {(workingStyle === FULL_REMOTE || workingStyle === PARTIAL_REMOTE) && (
                  <Box mb={3} alignItems="center">
                    <Box display="flex">
                      <img width="14" height="14" src="/assets/icons/house.png" alt="Working Style" />
                      <Text styleName="pLabelPrimary" ml={2}>
                        {t(`common:${workingStyle}`)}
                      </Text>
                    </Box>
                    {workingStyle === PARTIAL_REMOTE && (
                      <Box display="flex" alignItems="center" mb={3}>
                        <Text ml={4}>
                          <>
                            {numberOfRemoteDaysAllowed > 0
                              ? `${numberOfRemoteDaysAllowed} ${t('mission:daysPerWeekInRemote')}`
                              : t('mission:minimumNumberOfDaystoBeDiscussedWithClient')}
                          </>
                        </Text>
                      </Box>
                    )}
                  </Box>
                )}

                {(workingStyle === PARTIAL_REMOTE || workingStyle === ON_SITE) && (
                  <Box display="flex" alignItems="center" mb={3}>
                    <img width="14" height="14" src="/assets/icons/location.png" alt="location" />
                    <Text styleName="pLabelPrimary" ml={2}>
                      {location.getFullAddress()}
                    </Text>
                  </Box>
                )}

                <Box display="flex" alignItems="center" mb={3}>
                  <img width="14" height="14" src="/assets/icons/extra-time.png" alt="extra-time" />
                  <Text styleName="pLabelPrimary" ml={2}>
                    {extraTime ? t('mission:extraTimeAllowed') : t('mission:noExtraTimeAllowed')}
                  </Text>
                </Box>

                {['withCFMA', 'withoutCFMA'].includes(cfmaValue) && (
                  <Box display="flex" alignItems="center" mb={3}>
                    <Box mt="2px">
                      {cfmaValue === 'withCFMA' ? (
                        <img width="14" height="14" src="/assets/icons/file.png" alt="file" />
                      ) : (
                        <img width="14" height="14" src="/assets/icons/no-file.png" alt="no-file" />
                      )}
                    </Box>
                    <Box ml={2} flex="1">
                      <Text styleName="pLabelPrimary">{t(`mission:CFMA:${cfmaValue}`)}</Text>
                    </Box>
                  </Box>
                )}
              </Box>

              <Separator />

              <Box px={3} pb={3} style={{ wordBreak: 'break-word' }}>
                <SanitizedHTML content={description} />
              </Box>
            </Card>

            <Text styleName="pH2" mb={3}>
              {t('mission:requireSkills')}
            </Text>

            <Card px={3} pt={2} mb={4}>
              {Object.keys(skills).map(type => {
                //Do not display soft skill
                // #removeSoftSkill
                //TODO : delete this line or delete all soft skills
                if (type === 'soft') return null;
                const list = mission?.skills?.[type] || [];

                return list.length ? (
                  <Box mb={3} key={type} data-cy="skills">
                    <Text styleName="pLabelPrimary" mb={2}>
                      {t(`mission:skills_${type}`)}
                    </Text>
                    {list.map(skill => (
                      <SkillReadonly {...skill} key={skill.id} />
                    ))}
                  </Box>
                ) : null;
              })}
            </Card>

            {extraTime && (
              <>
                <Text styleName="pH2" mb={3}>
                  {t('mission:extraTime_title')}
                </Text>
                <Card mb={4}>
                  <Box display="flex" textAlign="center" justifyContent="flex-end" mt={2} mx={3}>
                    <Box flex="3" />
                    <Text flex="2" styleName="pPTiny" color="greyDark">
                      {t('mission:extraTime_DayPeriod')}
                    </Text>
                    <Text flex="2" styleName="pPTiny" color="greyDark">
                      {t('mission:extraTime_NightPeriod')}
                    </Text>
                  </Box>
                  <StyledList>
                    <li>
                      <Text styleName="pPBold">{t('mission:extraTime_mondayFriday')}</Text>
                      <Text styleName="pPBold" textAlign="center">
                        {extraTime.mondayFridayDay} %
                      </Text>
                      <Text styleName="pPBold" textAlign="center">
                        {extraTime.mondayFridayNight} %
                      </Text>
                    </li>
                    <li>
                      <Text styleName="pPBold">{t('mission:extraTime_saturday')}</Text>
                      <Text styleName="pPBold" textAlign="center">
                        {extraTime.saturdayDay} %
                      </Text>
                      <Text styleName="pPBold" textAlign="center">
                        {extraTime.saturdayNight} %
                      </Text>
                    </li>
                    <li>
                      <Text styleName="pPBold">{t('mission:extraTime_sundayOrHoliday')}</Text>
                      <Text styleName="pPBold" textAlign="center">
                        {extraTime.sundayOrHolidayDay} %
                      </Text>
                      <Text styleName="pPBold" textAlign="center">
                        {extraTime.sundayOrHolidayNight} %
                      </Text>
                    </li>
                  </StyledList>
                </Card>
              </>
            )}
          </Box>

          <Box
            width={{ _: '100%', lg: '280px', xl: '338px' }}
            position={{ _: 'static', lg: 'sticky' }}
            top="75px"
            textAlign="center"
            pb={2}>
            <Card mb={4} py={2}>
              {companyLogoLink && (
                <Box mb={2}>
                  <Avatar size={36} link={companyLogoLink} />
                </Box>
              )}
              <Text styleName="pLabelPrimary">{companyName}</Text>
              {(mission.responseRate || mission.responseRate === 0) && (
                <Text styleName="pPNormal" textAlign="center" my={1}>
                  {t('common:responseRate', { percentage: mission.responseRate })}
                </Text>
              )}
              <a
                data-cy="btn-mission-public-viewCompanyProfile"
                className="link"
                href={companyRoutes.companyProfile.to.replace(':companyId', companyId)}>
                {t('mission:viewProfile')}
              </a>
              <Separator mt={1} />
              <Text mt={2} styleName="pH1">
                {pricingModel === DAILY_RATE
                  ? money.formatRange(dailyRateRange, currency)
                  : money.format(fixedPrice, currency)}
              </Text>
              <Text fontSize="11px" color="greyDark" fontWeight="700">
                {pricingModel === DAILY_RATE ? t('mission:dailyPay') : t('mission:fixedPrice')}
              </Text>

              <Text fontSize="10px" fontStyle="italic" lineHeight="10px" color="grey">
                {t('mission:indicativeRate')}
              </Text>

              <ContentIO
                render={() => (
                  <CopyrightCalculatorButton data-cy="btn-mission-isLoggedIn-copyrightCalculator" mx={1} mt={2} />
                )}
              />

              <Separator mt={1} />

              <img width="16" height="16" src="/assets/icons/clock.png" alt="clock" />
              <Text styleName="pLabelPrimary">{getDisplayWorkTime(weeklyPlanning?.workTime, t)}</Text>
              {weeklyPlanning?.workTime === 'partTime' && (
                <Text styleName="pPNormal">
                  {t('common:hoursPerWeek', { hours: weeklyPlanning?.nbHourMaxByWeek || 0 })}
                </Text>
              )}

              <Separator />

              <img width="16" height="16" src="/assets/icons/calendar.png" alt="calendar" />
              <Box display="flex" justifyContent="space-evenly">
                <Text flex="1" styleName="wPTiny" textAlign="right" data-cy="mission-startDate">
                  {getMissionDate(startDate, t('mission:start_asap'), i18n.language)}
                </Text>
                <Text px={1} color="grey">
                  &#8226;&#32;&#8226;&#32;&#8226;
                </Text>
                <Text flex="1" styleName="wPTiny" textAlign="left" data-cy="mission-endDate">
                  {getMissionDate(endDate, t('mission:end_undetermined'), i18n.language)}
                </Text>
              </Box>
              <Box mt={2} mr={2} ml={2}>
                <Link type="tertiary" to={loginPathWithReturnUrl}>
                  <Button width="100%" data-cy="open-apply-modal">
                    {t('mission:apply')}
                  </Button>
                </Link>
              </Box>
            </Card>

            <Card px={3} py={2}>
              <Text mb={2} fontWeight="800">
                {t('mission:process.title')}
              </Text>
              <Box as="ul" textAlign="left">
                <Box as="li" display="flex" my={1} alignItems="center">
                  <IconBorder>
                    <img width="12" height="12" src="/assets/icons/log-in.png" alt="Apply" />
                  </IconBorder>
                  {t('mission:freelance.process.apply')}
                </Box>
                <Box as="li" display="flex" my={1} alignItems="center">
                  <IconBorder>
                    <img width="12" height="12" src="/assets/icons/message.png" alt="contact" />
                  </IconBorder>
                  {t('mission:freelance.process.contact')}
                </Box>
                <Box as="li" display="flex" my={1} alignItems="center">
                  <IconBorder>
                    <img width="12" height="12" src="/assets/icons/suitcase.png" alt="interview" />
                  </IconBorder>
                  {t('mission:freelance.process.interview')}
                </Box>
                <Box as="li" display="flex" my={1} alignItems="center">
                  <IconBorder>
                    <img width="12" height="12" src="/assets/icons/documents.png" alt="sign" />
                  </IconBorder>
                  {t('mission:freelance.process.sign')}
                </Box>
              </Box>
            </Card>

            <Box>
              <Text tag="h3" mt={2} mb={2} styleName="wPNormalLeft">
                {t('mission:share.title')}
              </Text>
              <Text tag="p" mb={2} styleName="wPNormalLeft">
                {t('mission:share.text')}
              </Text>
              <TwitterShareButton
                data-cy="btn-mission-public-twitterShare"
                onClick={() => emitEvent(EVENTS.MISSION.SHARE.TWITTER)}
                url={sharableMissionUrl}
                title={t('mission:share.twitter.freelance.title', { missionTitle: mission.title })}
                via="beelance_be"
                hashtags={(mission?.skills?.hard || []).map(({ text }) => text)}>
                <TwitterIcon size={40} bgStyle={{ fill: 'none' }} iconFillColor="#111517" className="ml-half" />
              </TwitterShareButton>
              <WhatsappShareButton
                data-cy="btn-mission-public-whatsappShare"
                onClick={() => emitEvent(EVENTS.MISSION.SHARE.WHATSAPP)}
                url={sharableMissionUrl}
                title={t('mission:share.whatsapp.freelance.title', { missionTitle: mission.title })}
                separator=" ">
                <WhatsappIcon size={35} bgStyle={{ fill: 'none' }} iconFillColor="#111517" className="ml-half" />
              </WhatsappShareButton>
              <FacebookShareButton
                data-cy="btn-mission-public-facebookShare"
                onClick={() => emitEvent(EVENTS.MISSION.SHARE.FACEBOOK)}
                url={sharableMissionUrl}
                quote={t('mission:share.facebook.freelance.quote', { missionTitle: mission.title })}
                hashtag="#Beelance">
                <FacebookIcon size={40} bgStyle={{ fill: 'none' }} iconFillColor="#111517" className="ml-half" />
              </FacebookShareButton>
            </Box>
          </Box>
        </Box>
        {mission.previousAndNext?.length > 1 && (
          <Box display="flex" mt={5} justifyContent="space-between">
            <a className="link" data-cy="previousMission" href={`${getAppUrl()}${i18n.language !== 'en' ? `/${i18n.language}` : ''}/missions/${mission.previousAndNext[0].slugWithCompany}`}>{`⊲ ${mission.previousAndNext[0].title}`}</a>
            <a className="link right" data-cy="nextMission" href={`${getAppUrl()}${i18n.language !== 'en' ? `/${i18n.language}` : ''}/missions/${mission.previousAndNext[1].slugWithCompany}`}>{`${mission.previousAndNext[1].title}  ⊳`}</a>
          </Box>
        )}
      </Box>
      {!user.isLoggedIn() && <ModalIncentivizeToRegister />}
    </Box>
  );
};

MissionSEO.propTypes = {
  previousMission: object,
  mission: object,
  nextMission: object
};

export default MissionSEO;
